/**
 * Wallet DTOs exposed by wallet-service `/api/accounts`, `/api/ledger` and
 * `/api/admin`. Mirrors the `WalletSnapshotResponse`, `AccountView`,
 * `AccountInDebtView`, `LedgerEntryResponse` records on the back.
 */

export type OwnerType = 'OWNER' | 'OPERATOR' | 'DRIVER' | 'PLATFORM';
export type AccountType = 'CREDIT' | 'BALANCE';
export type AccountStatus = 'ACTIVE' | 'FROZEN' | 'CLOSED';
export type LedgerDirection = 'DEBIT' | 'CREDIT';

export interface AccountView {
  id: number;
  ownerType: OwnerType;
  ownerRef: number;
  accountType: AccountType;
  /** BigDecimal serialized as number. */
  balance: number;
  currency: string;
  accountStatus: AccountStatus;
}

/** Consolidated view of CREDIT + BALANCE + withdrawable. */
export interface WalletSnapshotResponse {
  credit: AccountView;
  balance: AccountView;
  /** `balance.balance − max(0, −credit.balance)` */
  withdrawableBalance: number;
  /** Absolute debt if `credit.balance` < 0, else 0. */
  debt: number;
}

/** Row in the "accounts in debt" admin list. */
export interface AccountInDebtView {
  accountId: number;
  ownerType: OwnerType;
  ownerRef: number;
  balance: number;
  debt: number;
  /** Epoch ms or ISO date depending on serializer. */
  sinceUpdatedAt: string;
}

/** Ledger entry — history of credits/debits on an account. */
export interface LedgerEntryResponse {
  id: number;
  transferId?: number | null;
  accountId: number;
  direction: LedgerDirection;
  amount: number;
  resultingBalance: number;
  currency: string;
  postedAt: string;
  memo?: string | null;
}

// ── UI helpers ─────────────────────────────────────────────────────────

export const OWNER_TYPE_LABEL: Record<OwnerType, string> = {
  OWNER: 'Bailleur',
  OPERATOR: 'Opérateur',
  DRIVER: 'Chauffeur',
  PLATFORM: 'Plateforme',
};

export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  CREDIT: 'Crédit (réserve)',
  BALANCE: 'Balance (gains)',
};

export const ACCOUNT_STATUS_LABEL: Record<AccountStatus, string> = {
  ACTIVE: 'Actif',
  FROZEN: 'Gelé',
  CLOSED: 'Clôturé',
};

export const LEDGER_DIRECTION_LABEL: Record<LedgerDirection, string> = {
  DEBIT: 'Débit',
  CREDIT: 'Crédit',
};
